import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import AboutSideNav from '../../../components/about/AboutSideNav';
import { updateSEO, seoData } from '../../../utils/seo';

// ===== アクセス・所在地ページ 全面作り直し（Implementer A スコープ） =====
// デザイン土台: src/pages/about/overview/page.tsx（型の正典）と src/pages/home/page.tsx。
//   紺#1a2942 / 真鍮金#a38e65 / 金差し色見出し / リード3行+ / BTN-08 / 金eyebrow+和見出し。
//   セクション型は1ページ内で全て別型。overviewの型の単純コピーはしない。
//   一覧・重要データ（所在地/TEL/交通/所要時間）は大フォント（22px級・serif・強調色）で可読性を最優先。
// 事実正典（実データ変更禁止・創作禁止・出典path記録。外部実在との関わり主張は禁止）:
//   [C1] src/pages/about/access/page.tsx（旧・住所/TEL/法人番号/完全予約制/交通・車・駐車場・所要時間の実文）
//   [C2] mng-thebutler/00_事業説明.md L39,L102-103（お台場 青海2-7-4 / TEL 03-6824-4086 / 完全予約制拠点）※表示テキストに建物名(the SOHO)は出さない(Yuichi 2026-07-17)
//   [C3] src/utils/seo.ts /about/access（東京国際クルーズターミナル駅 徒歩5分）
//   固定データ: 〒135-0064 東京都江東区青海2丁目7−4 / TEL 03-6824-4086 / 法人番号 2010005039584 / 完全予約制

export default function AccessPage() {
  useEffect(() => {
    updateSEO(seoData['/about/access']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '協会について', href: '/about' },
    { label: 'アクセス・所在地' }
  ];

  const aboutNav = [
    { label: '協会概要', href: '/about/overview' },
    { label: '理念と使命', href: '/about/mission' },
    { label: '沿革・歴史', href: '/about/history' },
    { label: '組織体制', href: '/about/organization' },
    { label: '主な活動', href: '/about/activities' },
    { label: 'アクセス・所在地', href: '/about/access' }
  ];

  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  // [C1] 基本情報の細罫詳細（大フォント要点カードの下段）
  const detailRows = [
    { label: '法人名', value: '一般社団法人 全日本執事協会' },
    { label: '法人番号', value: '2010005039584' },
    { label: 'E-mail', value: 'info@butler-association.jp' },
    { label: '営業時間', value: '年中無休（ご来訪は完全予約制）' },
  ];

  // [C1][C3] 電車（三列カード・駅名/徒歩分を大フォント）
  const trainRoutes = [
    { line: 'ゆりかもめ', station: '東京国際クルーズターミナル駅', walk: '徒歩 約5分', notes: ['新橋駅から約15分', '豊洲駅から約10分', '駅を出て青海二丁目方面へ'] },
    { line: 'ゆりかもめ', station: 'テレコムセンター駅', walk: '徒歩 約5分', notes: ['新橋駅から約16分', '豊洲駅から約9分', '駅を出て青海方面へ'] },
    { line: 'りんかい線', station: '東京テレポート駅', walk: '徒歩 約15分', notes: ['大崎駅から約13分', '新木場駅から約8分', 'B出口から青海方面へ'] },
  ];

  // [C1] 主要駅からの所要時間（細罫・数値を大きめに）
  const travelTimes = [
    { from: '新橋駅', detail: 'ゆりかもめで約15分', fare: '運賃 330円' },
    { from: '豊洲駅', detail: 'ゆりかもめで約10分', fare: '運賃 270円' },
    { from: '品川駅', detail: 'りんかい線で約16分（大崎経由）', fare: '運賃 370円' },
    { from: '東京駅', detail: 'JR→ゆりかもめ乗り換えで約25分', fare: '' },
    { from: '渋谷駅', detail: '埼京線→りんかい線で約30分', fare: '' },
  ];

  // [C1] お車の高速ルート（定義リスト）
  const carRoutes = [
    { route: '首都高速湾岸線', detail: '臨海副都心出口より約5分／有明出口より約8分。湾岸線からが最もアクセスしやすいルートです。' },
    { route: '首都高速11号台場線', detail: '台場出口より約7分／有明出口より約10分。都心方面からはこちらが便利です。' },
    { route: '一般道から', detail: 'レインボーブリッジ経由、国道357号線（湾岸道路）経由、青海縦貫線利用でお越しいただけます。' },
  ];

  // [C1] 駐車場（積層2カード）
  const parkings = [
    { name: '当協会ビル内駐車場', tag: '有料・機械式収納型', lines: ['ご予約時にお申し出ください。台数に限りがございます。', '機械式のため車両サイズに制限があります。詳細はお電話にてご確認ください。', '満車の場合は近隣の駐車場をご利用ください。'] },
    { name: 'タイムズ青海２丁目', tag: '近隣・時間貸', lines: ['〒135-0064 東京都江東区青海２丁目２', '当協会より徒歩約3分', '24時間営業の時間貸駐車場です。', '料金等の詳細は駐車場運営会社にお問い合わせください。'] },
  ];

  // [C1] ご来訪の手順（横番号ステップ）
  const visitSteps = [
    { no: '01', title: '完全予約制', text: 'セミナー受講・会員サービス・各種ご相談など、当協会へのご来訪はすべて事前予約制です。質の高いご案内のための措置ですので、予約なしのご来訪はご遠慮ください。' },
    { no: '02', title: 'ご予約方法', text: 'お電話（03-6824-4086）またはメール（info@butler-association.jp）にて、お名前・ご連絡先、ご来訪の目的、ご希望日時（第1〜第3希望）、人数、お車の有無をお知らせください。' },
    { no: '03', title: 'ご来訪当日', text: 'ご予約時間の5分前を目安にお越しください。受付にてお名前とご用件をお伝えいただき、初めての方には館内をご案内します。遅刻・欠席の際は早めにお電話ください。' },
  ];

  return (
    <Layout>
      {/* SEO は updateSEO(seoData['/about/access']) を単一の正とする（JSX 二重定義は除去・H3） */}
      {/* ===== ヒーロー — 全幅ビルボード型 ===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/gen2/jba-access-aomi-office-exterior-01.jpg"
          alt="お台場・青海の協会本部を案内するための現代的なオフィス外観イメージ"
          className="absolute inset-0 w-full h-full object-cover object-[50%_15%]"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Access &amp; Location</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">東京湾を望む、</span>
              <span className="block">お台場の執事サロンへ</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              全日本執事協会の本部は、東京都江東区青海——お台場エリアの一角にあります。東京湾を望む国際色豊かな臨海副都心で、都内各所からのアクセスも良好です。ご来訪は完全予約制となっておりますので、事前にご連絡をお願いいたします。
            </p>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* ===== 本文 = サイドナビ + コンテンツの2カラム ===== */}
      <div className="bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20">
          <div className="grid lg:grid-cols-[248px_1fr] gap-8 lg:gap-14">

            <AboutSideNav items={aboutNav} activeHref="/about/access" />

            <div className="min-w-0 space-y-16 sm:space-y-20">

              {/* --- I. 所在地と基本情報（所在地要点カード型）= 新バリエーション①
                   由来注記: home VII 会員種別の細罫テーブル文法を、住所・電話・予約制を大フォント（22〜30px級）で
                   先出しする「要点カード」＋下段の細罫詳細テーブルの二層構成へ翻案。可読性を最優先。
                   overviewの定義リスト（均一15px値）とは階層・構図が異なるため新造。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">I. Location</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  協会本部・基本情報
                </h2>
                <div className="grid lg:grid-cols-[1fr_340px] gap-8 lg:gap-12 items-stretch">
                  {/* 要点カード（大フォント） */}
                  <div className="bg-[#1a2942] text-[#f8f6f0] p-8 sm:p-10 lg:p-12 flex flex-col justify-center">
                    <span className="inline-flex self-start items-center gap-2 border border-[#a38e65] text-[#d7b779] font-['Noto_Serif_JP',serif] text-xs tracking-[0.16em] px-4 py-1.5 mb-7">完全予約制</span>
                    <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.2em] text-[11px] text-[#a38e65] mb-2">Address</span>
                    <p className="jba-display font-medium text-[#f8f6f0] text-2xl sm:text-3xl lg:text-[32px] leading-[1.45] tracking-[0.02em] mb-1">
                      東京都江東区青海<br />2丁目7−4
                    </p>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] tracking-[0.06em] mb-8">〒135-0064</p>
                    <div className="border-t border-[#a38e65]/40 pt-7">
                      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.2em] text-[11px] text-[#a38e65] mb-2">Telephone</span>
                      <a href="tel:03-6824-4086" className="jba-display font-medium text-[#f8f6f0] text-2xl sm:text-[30px] tracking-[0.04em] hover:text-[#d7b779] transition-colors duration-300">03-6824-4086</a>
                      <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.85] mt-2">ご来訪・ご相談は、まずお電話またはメールでご予約ください。</p>
                    </div>
                  </div>
                  <img
                    src="/images/gen2/jba-access-aomi-office-exterior-02.jpg"
                    alt="全日本執事協会本部への来訪案内に用いる、青海周辺を想起させる現代的なオフィス外観イメージ"
                    className="w-full h-64 lg:h-full min-h-[260px] object-cover object-[50%_15%] border border-[#a38e65]/30"
                  />
                </div>
                {/* 細罫詳細テーブル */}
                <dl className="mt-8 border-t-2 border-[#1a2942] grid sm:grid-cols-2 gap-x-12">
                  {detailRows.map((row) => (
                    <div key={row.label} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                      <dt className="sm:w-[104px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">{row.label}</dt>
                      <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">{row.value}</dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-6 font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95] max-w-3xl">
                  当協会が本部を構える東京都江東区青海は、東京湾に面した先進的な臨海副都心・お台場の一角です。東京23区内からのアクセスが良好であるだけでなく、羽田空港からも約30分程度でお越しいただけるため、国内外からのご来訪にも便利な立地です。
                </p>
              </section>

              {/* --- II. 電車でお越しの方（三列カードグリッド型・駅名/徒歩分を大フォント）
                   由来: 共通カタログ N05 三列カードグリッド。重要データ（駅名・徒歩分）を大きく階層化し可読性を確保。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">II. By Train</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
                  電車でお越しの方
                </h2>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
                  お台場エリアは、ゆりかもめとりんかい線の2路線が乗り入れており、都内各所からのアクセスが便利です。当協会へは、ゆりかもめ「東京国際クルーズターミナル駅」または「テレコムセンター駅」のご利用が最も便利です。
                </p>
                <div className="grid md:grid-cols-3 gap-6 lg:gap-7 mb-10">
                  {trainRoutes.map((r, i) => (
                    <div key={i} className="border border-[#e2ddd2] bg-white p-7 flex flex-col">
                      <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1.5">{r.line}</span>
                      <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.45] mb-4">{r.station}</h3>
                      <p className="jba-display font-medium text-[#a38e65] text-xl sm:text-2xl lg:text-[26px] tracking-[0.02em] mb-5">{r.walk}</p>
                      <ul className="border-t border-[#e2ddd2] pt-4 space-y-1.5 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.8]">
                        {r.notes.map((n) => (<li key={n}>{n}</li>))}
                      </ul>
                    </div>
                  ))}
                </div>
                {/* 主要駅からの所要時間（大きめ数値の細罫リスト） */}
                <div className="bg-[#f8f6f0] border border-[#e2ddd2] p-7 sm:p-9">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-6">主要駅からの所要時間の目安</h3>
                  <dl className="border-t-2 border-[#1a2942]">
                    {travelTimes.map((t) => (
                      <div key={t.from} className="flex flex-wrap items-baseline gap-x-5 gap-y-1 border-b border-[#e2ddd2] py-4">
                        <dt className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl w-28 shrink-0">{t.from}</dt>
                        <dd className="flex-1 min-w-0 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#4a4638]">{t.detail}</dd>
                        {t.fare && <dd className="font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.04em]">{t.fare}</dd>}
                      </div>
                    ))}
                  </dl>
                </div>
              </section>

              {/* --- III. お車でお越しの方（定義リスト＋積層2カード）
                   由来: home IV エディトリアル行の罫線文法（高速ルート）＋共通カタログ N04 積層カード（駐車場）。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">III. By Car</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
                  お車でお越しの方
                </h2>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
                  お台場エリアは首都高速道路からのアクセスが良好です。ナビゲーションをご利用の際は、住所「東京都江東区青海2丁目7−4」または電話番号「03-6824-4086」をご入力ください。台数に限りがあるため、公共交通機関のご利用もあわせてご検討ください。
                </p>
                <dl className="border-t-2 border-[#1a2942] mb-10">
                  {carRoutes.map((c) => (
                    <div key={c.route} className="flex flex-col sm:flex-row gap-1.5 sm:gap-8 border-b border-[#e2ddd2] py-5">
                      <dt className="sm:w-[220px] sm:shrink-0 jba-display font-medium text-[#1a2942] text-lg leading-[1.4]">{c.route}</dt>
                      <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#4a4638] leading-[1.9]">{c.detail}</dd>
                    </div>
                  ))}
                </dl>
                <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-5">駐車場のご案内</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {parkings.map((p) => (
                    <div key={p.name} className="border border-[#e2ddd2] bg-white p-7">
                      <div className="flex items-baseline justify-between gap-3 mb-4 border-b border-[#e2ddd2] pb-4">
                        <h4 className="jba-display font-medium text-[#1a2942] text-lg leading-[1.4]">{p.name}</h4>
                        <span className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] shrink-0">{p.tag}</span>
                      </div>
                      <ul className="space-y-2 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]">
                        {p.lines.map((l) => (<li key={l} className="flex gap-2.5"><span className="text-[#a38e65] leading-[1.7]">—</span><span>{l}</span></li>))}
                      </ul>
                    </div>
                  ))}
                </div>
              </section>

              {/* --- IV. 周辺地図（地図＋注記帯）。由来: home のフレーム付き画像枠。素hrは使わない。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IV. Map</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  周辺地図
                </h2>
                <div className="border border-[#a38e65]/30">
                  <iframe
                    src="https://maps.google.com/maps?q=%E6%9D%B1%E4%BA%AC%E9%83%BD%E6%B1%9F%E6%9D%B1%E5%8C%BA%E9%9D%92%E6%B5%B72-7-4%20the%20SOHO&z=17&output=embed"
                    width="100%"
                    height="440"
                    style={{ border: 0, display: 'block' }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="全日本執事協会 本部 周辺地図"
                  ></iframe>
                  <p className="bg-[#f8f6f0] border-t border-[#e2ddd2] px-6 py-4 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]">
                    地図は目安です。詳細な道順は事前にご確認ください。初めてお越しの方で道に迷われた場合は、お気軽にお電話ください（TEL 03-6824-4086）。スタッフが丁寧にご案内いたします。
                  </p>
                </div>
              </section>

              {/* --- V. ご来訪の際のお願い（横番号ステップ型）= 新バリエーション②
                   由来注記: home の紺帯番号バンド（金番号+和見出し）の文法を、来訪手順の「横並び3ステップ」へ翻案。
                   overviewの縦タイムライン（年号の時系列）とは目的・構図が異なる（手順の順序案内）。トップに手順ステップ型は無いため新造。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">V. Before You Visit</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
                  ご来訪の際のお願い
                </h2>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
                  スムーズなご案内のため、ご来訪までの流れを3つのステップにまとめました。当協会へのご来訪はすべて完全予約制です。以下をご確認のうえ、事前にご連絡をお願いいたします。
                </p>
                <div className="grid md:grid-cols-3 gap-6 lg:gap-7">
                  {visitSteps.map((s) => (
                    <div key={s.no} className="border-t-2 border-[#a38e65] pt-6">
                      <span className="block font-['Playfair_Display',serif] text-[#d7b779] text-3xl sm:text-4xl leading-none mb-3">{s.no}</span>
                      <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-3 leading-[1.45]">{s.title}</h3>
                      <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.text}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* --- VI. 予約・問い合わせ導線（紺CTAパネル型）。由来: home / overview 締めCTAパネル --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VI. Reservations</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  ご来訪のご予約・お問い合わせ
                </h2>
                <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
                  <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                    当協会へのご来訪は完全予約制です。アクセス方法・駐車場・周辺情報など、ご不明な点がございましたら、お気軽にお問い合わせください。スタッフが丁寧にご案内いたします。
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <a href="tel:03-6824-4086" className={BTN08_ON_NAVY}>お電話でご予約 03-6824-4086</a>
                    <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせ</a>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
